import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Check, Copy, RefreshCw, X } from "lucide-react";
import { distributionsApi, keysApi, relaySitesApi, pushSitesApi, mainTemplatesApi, type DistributionExchange, type DistributionTask, type TaskStatus, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { TableShell, type ColumnSpec, type ListParams } from "@/components/TableShell";

// ---------------------------------------------------------------------------
// /tasks/list — 分发明细 · 审计视图(原型见 <repo>/demo-task.html)
//
// 侧边「分发记录」入口。也可由 /keys 的「→」带 ?key_id= 进入并自动过滤。列出分发
// 任务,可按时间 / 关键字 / 只看失败筛选,行展开看完整明细,失败可重试 / 放弃。
//
// ---------------------------------------------------------------------------

const TIME_WINDOWS = [
  { key: "all", label: "全部", ms: 0 },
  { key: "1h", label: "1h", ms: 3600_000 },
  { key: "24h", label: "24h", ms: 86_400_000 },
  { key: "7d", label: "7d", ms: 604_800_000 },
];

const STATUS_LABEL: Record<TaskStatus, string> = {
  staged: "暂存",
  pending: "待处理",
  dispatching: "分发中",
  success: "成功",
  failed: "失败",
  unknown: "未知",
  abandoned: "已放弃",
};
const STATUS_VARIANT: Record<TaskStatus, "default" | "success" | "warning" | "destructive" | "muted"> = {
  staged: "muted",
  pending: "muted",
  dispatching: "warning",
  success: "success",
  failed: "destructive",
  unknown: "warning",
  abandoned: "muted",
};

function formatClock(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function countMappingPairs(s: string): number {
  const v = (s || "").trim();
  if (v === "" || v === "{}" || v === "null") return 0;
  try {
    const parsed = JSON.parse(v);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return Object.keys(parsed).length;
  } catch {
    /* ignore */
  }
  return 0;
}

function prettyJSON(raw: string): string {
  const value = (raw || "").trim();
  if (value === "") return "";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return raw;
  }
}

function statusBadgeClass(status: number): string {
  if (status >= 200 && status < 300) return "bg-green-100 text-green-800";
  if (status === 0) return "bg-muted text-muted-foreground";
  if (status >= 500) return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-800";
}

function CopyTextButton({ text, title = "复制" }: { text: string; title?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      title={title}
      className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? "已复制" : "复制"}
    </button>
  );
}

function ExchangeBody({ label, body }: { label: string; body: string }) {
  const text = prettyJSON(body);
  return (
    <div className="min-w-0 overflow-hidden rounded-md border">
      <div className="flex items-center gap-2 border-b bg-muted/50 px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground">
        {label}
        {text && <CopyTextButton text={text} title={`复制${label}`} />}
      </div>
      <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-all bg-slate-900 p-2.5 font-mono text-[11px] leading-relaxed text-slate-100">
        {text || "（无内容）"}
      </pre>
    </div>
  );
}

function ExchangeCard({ exchange }: { exchange: DistributionExchange }) {
  return (
    <div className="rounded-md border bg-card p-2.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">#{exchange.seq}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">第 {exchange.attempt} 次</span>
        {exchange.adapter && <span className="rounded border px-1.5 py-0.5 text-[10px]">{exchange.adapter}</span>}
        <span className="font-mono text-[11px] font-semibold">{exchange.method || "—"}</span>
        <span className="min-w-[160px] flex-1 truncate font-mono text-[11px] text-muted-foreground" title={exchange.url}>{exchange.url || "—"}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadgeClass(exchange.response_status)}`}>
          {exchange.response_status > 0 ? `HTTP ${exchange.response_status}` : "无响应"}
        </span>
        <span className="text-[10px] text-muted-foreground">{exchange.duration_ms} ms</span>
      </div>
      {exchange.error_message && (
        <div className="mt-2 rounded border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-[11px] text-destructive">
          {exchange.error_message}
        </div>
      )}
      {exchange.request_headers && (
        <details className="mt-2 text-[11px] text-muted-foreground">
          <summary className="cursor-pointer select-none">请求 Headers</summary>
          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-[10px]">
            {prettyJSON(exchange.request_headers)}
          </pre>
        </details>
      )}
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        <ExchangeBody label="REQUEST" body={exchange.request_body} />
        <ExchangeBody label="RESPONSE" body={exchange.response_body} />
      </div>
    </div>
  );
}

function TaskExchangePanel({ taskId }: { taskId: number }) {
  const q = useQuery({
    queryKey: ["tasks", "detail", taskId],
    queryFn: () => distributionsApi.get(taskId),
  });
  const exchanges = q.data?.exchanges ?? [];

  return (
    <div className="mt-3 rounded-md border bg-background">
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-3 py-2">
        <span className="text-xs font-semibold">请求 / 响应</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">已脱敏</span>
        {q.isFetching && <span className="text-[10px] text-muted-foreground">加载中…</span>}
        <span className="ml-auto text-[10px] text-muted-foreground">共 {exchanges.length} 次调用</span>
        <button type="button" className="rounded border px-2 py-0.5 text-[10px] hover:bg-accent" onClick={() => q.refetch()}>
          刷新
        </button>
      </div>
      <div className="space-y-2 p-3">
        {q.isLoading && <div className="py-4 text-center text-xs text-muted-foreground">加载请求/响应…</div>}
        {q.isError && <div className="py-2 text-xs text-destructive">加载失败:{q.error instanceof Error ? q.error.message : "未知错误"}</div>}
        {!q.isLoading && !q.isError && exchanges.length === 0 && (
          <div className="py-4 text-center text-xs text-muted-foreground">暂无请求/响应记录（历史任务或适配器未发起 HTTP 请求）</div>
        )}
        {exchanges.map((exchange, index) => (
          <ExchangeCard key={`${exchange.attempt}-${exchange.seq}-${index}`} exchange={exchange} />
        ))}
      </div>
    </div>
  );
}

export function TaskListPage({ siteType = "relay" as "relay" | "push" }: { siteType?: "relay" | "push" }) {
  const isRelay = siteType === "relay";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { key_id?: number };
  const keyIdFilter = search.key_id;

  const [params, setParams] = useState<ListParams>({ page: 1, page_size: 30 });
  const [timeWin, setTimeWin] = useState(keyIdFilter ? "all" : "24h");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [templateFilter, setTemplateFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { if (keyIdFilter) setTimeWin("all"); }, [keyIdFilter]);
  // 独立「任务 ID」搜索框:精确匹配 distribution_task.id。输入防抖后再作为过滤器,
  // 避免逐字符触发重挂载/请求。
  const [taskIdInput, setTaskIdInput] = useState("");
  const [taskIdFilter, setTaskIdFilter] = useState("");
  useEffect(() => {
    if (taskIdInput === taskIdFilter) return;
    const timer = window.setTimeout(() => setTaskIdFilter(taskIdInput), 250);
    return () => window.clearTimeout(timer);
  }, [taskIdInput, taskIdFilter]);
  const createdAfter = useMemo(() => {
    const windowMs = TIME_WINDOWS.find((item) => item.key === timeWin)?.ms ?? 0;
    return windowMs > 0 ? new Date(Date.now() - windowMs).toISOString() : undefined;
  }, [timeWin]);
  const tasksQ = useQuery({
    queryKey: [
      "tasks",
      isRelay ? "relay" : "push",
      keyIdFilter ?? "all",
      taskIdFilter,
      params.page,
      params.page_size,
      params.sort ?? "",
      params.order ?? "",
      params.q ?? "",
      timeWin,
      onlyFailed,
      templateFilter,
      siteFilter,
      statusFilter,
    ],
    queryFn: () => distributionsApi.list({
      site_type: siteType,
      ...(keyIdFilter ? { key_id: keyIdFilter } : {}),
      ...(taskIdFilter ? { ids: [Number(taskIdFilter)] } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(templateFilter ? { template_id: Number(templateFilter) } : {}),
      ...(siteFilter ? { site_id: Number(siteFilter) } : {}),
      // 按任务 ID 精确查找时不受时间窗限制,避免历史任务被默认 24h 挡住。
      ...(createdAfter && !taskIdFilter ? { created_after: createdAfter } : {}),
      ...(onlyFailed ? { status: ["failed", "unknown"] as TaskStatus[] } : statusFilter ? { status: statusFilter as TaskStatus } : {}),
      page: params.page,
      page_size: params.page_size,
      sort: params.sort,
      order: params.order,
    }),
  });
  const taskKeyIDs = useMemo(
    () => Array.from(new Set((tasksQ.data?.items ?? []).map((task) => task.key_id).concat(keyIdFilter && !tasksQ.data?.items.length ? [keyIdFilter] : []))),
    [tasksQ.data, keyIdFilter],
  );
  const keysQ = useQuery({
    queryKey: ["keys", "task-page", taskKeyIDs],
    queryFn: () => keysApi.list({ ids: taskKeyIDs, page: 1, page_size: Math.max(1, taskKeyIDs.length) }),
    enabled: taskKeyIDs.length > 0,
  });
  const relaySitesQ = useQuery({ queryKey: ["relay-sites"], queryFn: relaySitesApi.list, enabled: isRelay });
  const pushSiteQ = useQuery({ queryKey: ["push-sites"], queryFn: pushSitesApi.list, enabled: !isRelay });
  const templatesQ = useQuery({ queryKey: ["main-templates"], queryFn: () => mainTemplatesApi.list() });
  const sitesQ = isRelay ? relaySitesQ : pushSiteQ;

  const keyById = useMemo(() => {
    const m = new Map<number, string>();
    keysQ.data?.items.forEach((k) => m.set(k.id, k.name));
    return m;
  }, [keysQ.data]);
  const siteById = useMemo(() => {
    const m = new Map<number, string>();
    sitesQ.data?.items.forEach((s) => m.set(s.id, s.name));
    return m;
  }, [sitesQ.data]);
  const templateById = useMemo(() => new Map((templatesQ.data?.items ?? []).map((t) => [t.id, t.name])), [templatesQ.data]);

  // site_type 已在后端过滤;TableShell server 模式只处理当前这一页。
  const scopedTasks = tasksQ.data?.items ?? [];

  const keyName = (id: number) => keyById.get(id) ?? `#${id}`;
  const siteName = (id: number) => siteById.get(id) ?? `#${id}`;

  const total = tasksQ.data?.total ?? 0;
  const failedCount = scopedTasks.filter((t) => t.status === "failed" || t.status === "unknown").length;

  const taskColumns: ColumnSpec<DistributionTask>[] = [
    { key: "id", label: "ID", sortable: true },
    { key: "key_id", label: "Key", sortable: true },
    { key: "template_id", label: "主模板" },
    { key: "site_id", label: "站点", sortable: true },
    { key: "status", label: "状态", sortable: true },
    { key: "error_message", label: "错误信息" },
    { key: "dispatched_at", label: "派发时间", sortable: true },
    { key: "actions", label: "操作", align: "center" },
  ];

  const retryMut = useMutation({
    mutationFn: (id: number) => distributionsApi.retry(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); setMsg("已触发重试"); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "重试失败"),
  });
  const abandonMut = useMutation({
    mutationFn: (id: number) => distributionsApi.abandon(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); setMsg("已放弃该任务"); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "放弃失败"),
  });

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{isRelay ? "中转站" : "推送站"}分发明细</h1>
        <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">审计视图</span>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              tasksQ.refetch();
              if (taskKeyIDs.length > 0) keysQ.refetch();
              sitesQ.refetch();
              templatesQ.refetch();
            }}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${tasksQ.isFetching ? "animate-spin" : ""}`} />
            手动刷新
          </button>
          {/* 导出按钮移到导出功能接入时再加;现放着灰色 disabled 容易让运营误以为坏了 */}
        </div>
      </div>

      {/* Filter banner */}
      {keyIdFilter != null && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs text-primary">
            当前过滤:Key = {keyName(keyIdFilter)}(ID {keyIdFilter})
            <button type="button" onClick={() => navigate({ to: isRelay ? "/relay/tasks/list" : "/push/tasks/list", search: {} })} aria-label="清除过滤">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
          <span className="ml-auto text-xs text-muted-foreground">从 Key 明细点击「→」自动带入此过滤</span>
        </div>
      )}

      {msg && <div className="text-sm text-muted-foreground">{msg}</div>}
      {tasksQ.isError && <div className="text-sm text-destructive">任务加载失败：{tasksQ.error instanceof Error ? tasksQ.error.message : "未知错误"}</div>}
      {(keysQ.isError || sitesQ.isError || templatesQ.isError) && <div className="text-sm text-destructive">关联数据加载失败，部分名称显示为 ID。</div>}

      {/* Toolbar + Table */}
      <div className="rounded-xl border bg-card p-3 shadow-sm">
        <TableShell
          key={`${siteType}|${keyIdFilter ?? "all"}`}
          mode="server"
          columns={taskColumns}
          data={scopedTasks}
          total={tasksQ.data?.total}
          loading={tasksQ.isLoading}
          defaultPageSize={30}
          pageResetKey={`${taskIdFilter}|${timeWin}|${onlyFailed}|${templateFilter}|${siteFilter}|${statusFilter}`}
          onParamsChange={setParams}
          emptyText="暂无分发记录"
          search={{ placeholder: "搜索 Key / 站点…" }}
          searchPrefix={
            <Input
              value={taskIdInput}
              onChange={(e) => setTaskIdInput(e.target.value.replace(/\D/g, ""))}
              placeholder="任务 ID(精确)"
              inputMode="numeric"
              aria-label="按任务 ID 搜索"
              className="h-9 w-36 text-sm"
            />
          }
          filters={
            <div className="flex flex-wrap items-center gap-2">
              <select aria-label="筛选主模板" className="h-9 rounded-md border bg-background px-2 text-sm" value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}>
                <option value="">全部主模板</option>
                {(templatesQ.data?.items ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select aria-label="筛选目标站点" className="h-9 rounded-md border bg-background px-2 text-sm" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
                <option value="">全部{isRelay ? "中转站" : "推送站"}</option>
                {(sitesQ.data?.items ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select aria-label="筛选任务状态" className="h-9 rounded-md border bg-background px-2 text-sm" value={statusFilter} disabled={onlyFailed} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">全部状态</option>
                {(Object.keys(STATUS_LABEL) as TaskStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
              {TIME_WINDOWS.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => setTimeWin(w.key)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                    timeWin === w.key ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`}
                >
                  {w.label}
                </button>
              ))}
              <span className="mx-1 text-xs text-muted-foreground">|</span>
              <button
                type="button"
                onClick={() => setOnlyFailed((v) => !v)}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
                  onlyFailed ? "border-destructive bg-destructive text-destructive-foreground" : "hover:bg-accent"
                }`}
              >
                {onlyFailed ? "显示全部" : "只看失败"}
              </button>
              {taskIdFilter && <span className="text-xs text-muted-foreground">ID 搜索不受时间窗限制</span>}
              <span className="text-xs text-muted-foreground">本页 {scopedTasks.length} 条 · 失败 {failedCount} 条</span>
            </div>
          }
          rowKey={(t) => t.id}
          renderRow={(t) => {
            const isExpanded = expanded === t.id;
            const canReconcile = t.site_type === "push" && t.status === "unknown" && (t.local_template_id ?? 0) > 0;
            const canRetry = (t.status === "failed" || t.status === "unknown") &&
              (t.retry_count < t.max_retries || canReconcile);
            const canAbandon = t.status === "failed" || t.status === "unknown";
            const mmCount = countMappingPairs(t.model_mapping);
            return (
              <>
                <tr
                  className={`cursor-pointer border-b hover:bg-muted/30 ${isExpanded ? "bg-muted/30" : ""}`}
                  onClick={() => setExpanded(isExpanded ? null : t.id)}
                >
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{t.id}</td>
                  <td className="px-3 py-2 font-medium"><button type="button" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); navigate({ to: "/keys", search: { key_id: t.key_id } }); }}>{keyName(t.key_id)}</button></td>
                  <td className="px-3 py-2 text-muted-foreground">{t.template_id ? templateById.get(t.template_id) ?? `#${t.template_id}` : "未知"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{siteName(t.site_id)}</td>
                  <td className="px-3 py-2"><Badge variant={STATUS_VARIANT[t.status]}>{STATUS_LABEL[t.status]}</Badge></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {t.error_message ? (
                      <span className="inline-block max-w-[220px] truncate text-destructive" title={t.error_message}>{t.error_message}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{formatClock(t.dispatched_at ?? t.created_at)}</td>
                  <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                    {canRetry && (
                      <Button size="sm" variant="outline" disabled={retryMut.isPending} onClick={() => retryMut.mutate(t.id)}>重试</Button>
                    )}
                    {canAbandon && (
                      <Button size="sm" variant="ghost" disabled={abandonMut.isPending} onClick={() => { if (confirm(`确认放弃任务 #${t.id}?该操作不可撤销`)) abandonMut.mutate(t.id); }}>放弃</Button>
                    )}
                    {!canRetry && !canAbandon && (
                      <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setExpanded(isExpanded ? null : t.id)}>详情</button>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-b bg-muted/20">
                    <td colSpan={8} className="px-3 py-2">
                      <div className="grid grid-cols-3 gap-x-6 gap-y-1 rounded-md bg-muted/40 px-3 py-2 text-xs">
                        <div><span className="text-muted-foreground">分组</span> <span className="font-mono">{t.groups || "—"}</span></div>
                        <div><span className="text-muted-foreground">优先级</span> <span className="font-mono">{t.priority ?? "—"}</span></div>
                        <div>
                          <span className="text-muted-foreground">模型映射</span>{" "}
                          {mmCount === 0 ? <span className="font-mono">—</span> : <span className="font-mono">{mmCount} 项</span>}
                        </div>
                        <div><span className="text-muted-foreground">Diff</span> <span className="font-mono">{t.update_diff || "—"}</span></div>
                        <div><span className="text-muted-foreground">远端ID</span> <span className="font-mono">{t.remote_channel_id ?? "—"}</span></div>
                        <div><span className="text-muted-foreground">重试次数</span> <span className="font-mono">{t.retry_count}/{t.max_retries}</span></div>
                      </div>
                      {mmCount > 0 && (
                        <pre className="mt-2 max-w-full overflow-auto rounded bg-muted p-2 text-[11px]">{t.model_mapping}</pre>
                      )}
                      <TaskExchangePanel taskId={t.id} />
                    </td>
                  </tr>
                )}
              </>
            );
          }}
        />
      </div>

      <div className="text-xs text-muted-foreground">💡 点击行展开查看完整详情(分组 / 优先级 / 模型映射 / Diff / 远端ID / 重试次数 / 请求响应)。</div>
    </div>
  );
}
